"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { Link } from "@/i18n/navigation";
import { withMask } from "use-mask-input";
import { X, Loader2, CheckCircle2, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import type { FormData } from "./page";
import { env } from "@/lib/env";
import { useAnalytics } from "@/lib/analytics";
import { getPageTotalPrice } from "@/lib/pricing";
import { useTranslations } from "next-intl";

/** Validates Brazilian CPF (11 digits, check digits). */
function isValidCPF(digits: string): boolean {
	if (!/^\d{11}$/.test(digits)) return false;
	if (/^(\d)\1{10}$/.test(digits)) return false; // all same digit
	let sum = 0;
	for (let i = 0; i < 9; i++) {
		const d = digits[i];
		if (d === undefined) return false;
		sum += parseInt(d, 10) * (10 - i);
	}
	let rest = (sum * 10) % 11;
	if (rest === 10) rest = 0;
	const d9 = digits[9];
	if (d9 === undefined || rest !== parseInt(d9, 10)) return false;
	sum = 0;
	for (let i = 0; i < 10; i++) {
		const d = digits[i];
		if (d === undefined) return false;
		sum += parseInt(d, 10) * (11 - i);
	}
	rest = (sum * 10) % 11;
	if (rest === 10) rest = 0;
	const d10 = digits[10];
	return d10 !== undefined && rest === parseInt(d10, 10);
}

/**
 * Strict email validation (aligns with common APIs like Asaas).
 * Rejects: leading/trailing dots in local part, consecutive dots,
 * invalid domain labels, TLD < 2 chars.
 */
function isValidEmail(email: string): boolean {
	const s = email.trim();
	if (!s || s.length > 254) return false;
	const at = s.indexOf("@");
	if (at <= 0 || at >= s.length - 1) return false;
	const local = s.slice(0, at);
	const domain = s.slice(at + 1);

	// Local part: 1–64 chars, no leading/trailing dot, no consecutive dots
	if (local.length > 64) return false;
	if (local.startsWith(".") || local.endsWith(".")) return false;
	if (local.includes("..")) return false;
	if (!/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/.test(local)) return false;

	// Domain: at least one dot, no leading/trailing dot, no consecutive dots
	if (!domain.includes(".") || domain.startsWith(".") || domain.endsWith("."))
		return false;
	if (domain.includes("..")) return false;
	const labels = domain.split(".");
	const tld = labels[labels.length - 1];
	if (!tld || tld.length < 2) return false;
	for (const label of labels) {
		if (!label || label.length > 63) return false;
		if (label.startsWith("-") || label.endsWith("-")) return false;
		if (!/^[a-zA-Z0-9-]+$/.test(label)) return false;
	}
	return true;
}

type PaymentStep = "form" | "loading" | "qrcode" | "creating";

export function PaymentModal({
	open,
	onClose,
	form,
	couponCode,
}: {
	open: boolean;
	onClose: () => void;
	form: FormData;
	couponCode?: string;
}) {
	const t = useTranslations("PaymentModal");
	const common = useTranslations("Common");
	const { trackPaymentInfoAdded } = useAnalytics();
	
	const [paymentStep, setPaymentStep] = useState<PaymentStep>("form");
	const [paymentForm, setPaymentForm] = useState({
		name: "",
		cpfCnpj: "",
		email: "",
	});
	const [paymentId, setPaymentId] = useState<string | null>(null);
	const [pixQrCode, setPixQrCode] = useState<{
		encodedImage: string;
		payload: string;
		expirationDate: string;
	} | null>(null);
	const [paymentError, setPaymentError] = useState<string | null>(null);
	const [cpfError, setCpfError] = useState<string | null>(null);
	const [emailError, setEmailError] = useState<string | null>(null);
	const [pixCountdownSeconds, setPixCountdownSeconds] = useState(0);
	const [pixCopied, setPixCopied] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const [createdPageSlug, setCreatedPageSlug] = useState<string | null>(null);
	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const uploadStartedRef = useRef(false);
	const pageCreateStartedRef = useRef(false);
	const confirmedPaymentIdRef = useRef<string | null>(null);
	const normalizedCouponCode = couponCode?.trim() ?? "";
	const hasCouponCode = normalizedCouponCode.length > 0;

	// Reset state when modal opens
	useEffect(() => {
		if (open) {
			setPaymentStep("form");
			setPaymentId(null);
			setPixQrCode(null);
			setPaymentError(null);
			setPixCountdownSeconds(0);
			setPixCopied(false);
			setUploadProgress(0);
			setUploadError(null);
			setCreatedPageSlug(null);
			setPaymentForm({ name: "", cpfCnpj: "", email: "" });
			setCpfError(null);
			setEmailError(null);
			uploadStartedRef.current = false;
			pageCreateStartedRef.current = false;
			confirmedPaymentIdRef.current = null;
		}
	}, [open]);

	// Poll payment status when showing QR code
	useEffect(() => {
		if (!open || paymentStep !== "qrcode" || !paymentId) return;
		const poll = () => {
			fetch(`/api/payment/${paymentId}/status`)
				.then((r) => r.json())
				.then((data: { status?: string }) => {
					const status = data?.status;
					if (status === "RECEIVED" || status === "CONFIRMED") {
						if (pollRef.current) {
							clearInterval(pollRef.current);
							pollRef.current = null;
						}
						// Save paymentId before clearing for use in page creation
						confirmedPaymentIdRef.current = paymentId;
						setPaymentStep("creating");
						setPaymentId(null);
						setPixQrCode(null);
					}
				})
				.catch(() => {});
		};
		pollRef.current = setInterval(poll, 3000);
		poll();
		return () => {
			if (pollRef.current) clearInterval(pollRef.current);
		};
	}, [open, paymentStep, paymentId]);

	// 15-minute countdown when showing QR code
	useEffect(() => {
		if (!open || paymentStep !== "qrcode") return;
		const id = setInterval(() => {
			setPixCountdownSeconds((s) => {
				if (s <= 1) {
					clearInterval(id);
					return 0;
				}
				return s - 1;
			});
		}, 1000);
		return () => clearInterval(id);
	}, [open, paymentStep]);

	const createPageInDb = useCallback(
		async (imageKeys: string[], confirmedPaymentId: string) => {
			if (pageCreateStartedRef.current) return;
			pageCreateStartedRef.current = true;
			try {
				const createRes = await fetch("/api/page/create", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						slug: form.slug?.trim(),
						coupleName1: form.coupleName1,
						coupleName2: form.coupleName2,
						title: form.title,
						message: form.message,
						relationshipDate: form.relationshipDate,
						musicUrl: form.musicUrl || "",
						theme: form.theme,
						imageKeys,
						paymentId: confirmedPaymentId,
					}),
				});
				const data = (await createRes.json()) as {
					error?: string;
					slug?: string;
				};
				if (!createRes.ok) {
					setUploadError(data?.error ?? t("errors.createPageError"));
					return;
				}
				if (data.slug) setCreatedPageSlug(data.slug);
			} catch (e) {
				setUploadError(
					e instanceof Error ? e.message : t("errors.createPageError"),
				);
			} finally {
				pageCreateStartedRef.current = false;
			}
		},
		[
			form.slug,
			form.coupleName1,
			form.coupleName2,
			form.title,
			form.message,
			form.relationshipDate,
			form.musicUrl,
			form.theme,
			t,
		],
	);

	// Upload images to S3 when payment is confirmed, then create page in DB
	useEffect(() => {
		if (!open || paymentStep !== "creating" || uploadStartedRef.current) return;
		const imageFiles = form.imageFiles;
		const slug = form.slug?.trim();
		const confirmedPaymentId = confirmedPaymentIdRef.current;
		if (!slug || !confirmedPaymentId) return;

		uploadStartedRef.current = true;
		setUploadError(null);
		setUploadProgress(0);
		setCreatedPageSlug(null);

		if (imageFiles.length === 0) {
			setUploadProgress(100);
			createPageInDb([], confirmedPaymentId);
			return;
		}

		let cancelled = false;
		(async () => {
			try {
				const presignRes = await fetch("/api/upload/presign", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						slug,
						files: imageFiles.map((f) => ({
							contentType: f.type || "application/octet-stream",
						})),
					}),
				});
				const presignData = await presignRes.json();
				if (!presignRes.ok) {
					throw new Error(presignData?.error ?? t("errors.uploadError"));
				}
				const { uploads } = presignData as {
					uploads: { url: string; key: string }[];
				};
				const total = uploads.length;
				for (let i = 0; i < total && !cancelled; i++) {
					const res = await fetch(uploads[i].url, {
						method: "PUT",
						body: imageFiles[i],
						headers: {
							"Content-Type": imageFiles[i].type || "application/octet-stream",
						},
					});
					if (!res.ok) {
						throw new Error(t("errors.uploadImageError", { number: i + 1 }));
					}
					setUploadProgress(Math.round(((i + 1) / total) * 100));
				}
				if (!cancelled) {
					await createPageInDb(
						uploads.map((u) => u.key),
						confirmedPaymentId,
					);
				}
			} catch (e) {
				if (!cancelled) {
					setUploadError(e instanceof Error ? e.message : t("errors.uploadError"));
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [open, paymentStep, form.slug, form.imageFiles, createPageInDb, t]);

	const handleGenerateQrCode = useCallback(async () => {
		if (!paymentForm.name.trim() || !paymentForm.cpfCnpj.trim()) {
			setPaymentError(t("validation.nameAndCpfRequired"));
			return;
		}
		const cpfDigits = paymentForm.cpfCnpj.replace(/\D/g, "");
		if (cpfDigits.length !== 11) {
			setCpfError(t("validation.cpfDigits"));
			return;
		}
		if (!isValidCPF(cpfDigits)) {
			setCpfError(t("validation.invalidCpf"));
			return;
		}
		const email = paymentForm.email.trim();
		if (email && !isValidEmail(email)) {
			setEmailError(t("validation.invalidEmail"));
			return;
		}
		setPaymentError(null);
		setCpfError(null);
		setEmailError(null);

		setPaymentStep("loading");
		try {
			if (hasCouponCode) {
				const couponRes = await fetch("/api/coupon/redeem", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						code: normalizedCouponCode,
						name: paymentForm.name.trim(),
						cpfCnpj: paymentForm.cpfCnpj.replace(/\D/g, ""),
						email: paymentForm.email.trim() || undefined,
						imageCount: form.imageFiles.length,
					}),
				});
				const couponData = await couponRes.json();
				if (!couponRes.ok) {
					throw new Error(couponData?.error ?? t("errors.couponRedeemError"));
				}

				const { paymentId: id } = couponData as { paymentId: string };
				confirmedPaymentIdRef.current = id;
				setPaymentStep("creating");
				return;
			}

			const value = getPageTotalPrice(form.imageFiles.length);
			trackPaymentInfoAdded(value, form.imageFiles.length);

			const checkoutRes = await fetch("/api/checkout", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					name: paymentForm.name.trim(),
					cpfCnpj: paymentForm.cpfCnpj.replace(/\D/g, ""),
					email: paymentForm.email.trim() || undefined,
					imageCount: form.imageFiles.length,
				}),
			});
			const checkoutData = await checkoutRes.json();
			if (!checkoutRes.ok) {
				throw new Error(checkoutData?.error ?? t("errors.checkoutError"));
			}
			const { paymentId: id } = checkoutData as { paymentId: string };
			setPaymentId(id);
			const qrRes = await fetch(`/api/payment/${id}/pix-qrcode`);
			const qrData = await qrRes.json();
			if (!qrRes.ok) {
				throw new Error(qrData?.error ?? t("errors.qrCodeError"));
			}
			setPixQrCode({
				encodedImage: qrData.encodedImage,
				payload: qrData.payload,
				expirationDate: qrData.expirationDate,
			});
			setPixCountdownSeconds(15 * 60);
			setPaymentStep("qrcode");
		} catch (e) {
			setPaymentError(e instanceof Error ? e.message : t("errors.qrCodeError"));
			setPaymentStep("form");
		}
	}, [
		paymentForm,
		form.imageFiles.length,
		t,
		trackPaymentInfoAdded,
		hasCouponCode,
		normalizedCouponCode,
	]);

	const closePaywall = useCallback(() => {
		if (pollRef.current) {
			clearInterval(pollRef.current);
			pollRef.current = null;
		}
		setPaymentStep("form");
		setPaymentId(null);
		setPixQrCode(null);
		setPaymentError(null);
		setPixCountdownSeconds(0);
		setPixCopied(false);
		setUploadProgress(0);
		setUploadError(null);
		setCreatedPageSlug(null);
		uploadStartedRef.current = false;
		pageCreateStartedRef.current = false;
		confirmedPaymentIdRef.current = null;
		onClose();
	}, [onClose]);

	if (!open) return null;

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
			role="dialog"
			aria-modal="true"
			aria-labelledby={paymentStep === "creating" ? undefined : "paywall-title"}
			onClick={(e) =>
				paymentStep === "form" && e.target === e.currentTarget && closePaywall()
			}
			onKeyDown={(e) =>
				paymentStep === "form" && e.key === "Escape" && closePaywall()
			}
		>
			<div className="relative w-full max-w-md rounded-2xl border border-border bg-background p-6 shadow-xl">
				{paymentStep === "form" && (
					<button
						type="button"
						onClick={closePaywall}
						className="absolute right-4 top-4 rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
						aria-label={common("close")}
					>
						<X className="h-5 w-5" />
					</button>
				)}
				{paymentStep !== "creating" && (
					<h2 id="paywall-title" className="mb-4 pr-8 text-lg font-semibold">
						{t("title")}
					</h2>
				)}

				{paymentStep === "form" && (
					<div className="space-y-4">
						<p className="text-sm text-muted-foreground">
							{t("description")}
						</p>
						<div className="space-y-2">
							<Label htmlFor="pay-name">{t("form.fullName")}</Label>
							<Input
								id="pay-name"
								placeholder={t("form.fullNamePlaceholder")}
								value={paymentForm.name}
								onChange={(e) =>
									setPaymentForm((p) => ({
										...p,
										name: e.target.value,
									}))
								}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="pay-cpf">{t("form.cpf")}</Label>
							<Input
								id="pay-cpf"
								ref={withMask("cpf", { placeholder: t("form.cpfPlaceholder") })}
								placeholder={t("form.cpfPlaceholder")}
								value={paymentForm.cpfCnpj}
								onChange={(e) => {
									setCpfError(null);
									setPaymentForm((p) => ({
										...p,
										cpfCnpj: e.target.value,
									}));
								}}
								onBlur={() => {
									const digits = paymentForm.cpfCnpj.replace(/\D/g, "");
									if (digits.length === 0) return;
									if (digits.length !== 11) {
										setCpfError(t("validation.cpfDigits"));
										return;
									}
									if (!isValidCPF(digits)) setCpfError(t("validation.invalidCpf"));
								}}
								aria-invalid={!!cpfError}
							/>
							{cpfError && (
								<p className="text-sm text-destructive">{cpfError}</p>
							)}
						</div>
						<div className="space-y-2">
							<Label htmlFor="pay-email">{t("form.email")}</Label>
							<Input
								id="pay-email"
								type="email"
								placeholder={t("form.emailPlaceholder")}
								value={paymentForm.email}
								onChange={(e) => {
									setEmailError(null);
									setPaymentForm((p) => ({
										...p,
										email: e.target.value,
									}));
								}}
								onBlur={() => {
									const email = paymentForm.email.trim();
									if (email.length === 0) return;
									if (!isValidEmail(email)) setEmailError(t("validation.invalidEmail"));
								}}
								aria-invalid={!!emailError}
							/>
							{emailError && (
								<p className="text-sm text-destructive">{emailError}</p>
							)}
						</div>
						{paymentError && (
							<p className="text-sm text-destructive">{paymentError}</p>
						)}
						<Button
							type="button"
							className="w-full"
							disabled={
								!paymentForm.name.trim() ||
								!paymentForm.cpfCnpj.trim() ||
								!paymentForm.email.trim() ||
								!!cpfError ||
								!!emailError ||
								paymentForm.cpfCnpj.replace(/\D/g, "").length !== 11 ||
								!isValidCPF(paymentForm.cpfCnpj.replace(/\D/g, "")) ||
								!isValidEmail(paymentForm.email.trim())
							}
							onClick={handleGenerateQrCode}
						>
							<QrCode className="size-4" />
							{hasCouponCode ? t("redeemCoupon") : t("generateQrCode")}
						</Button>
					</div>
				)}

				{paymentStep === "loading" && (
					<div className="flex flex-col items-center justify-center gap-4 py-8">
						<Loader2 className="h-10 w-10 animate-spin text-primary" />
						<p className="text-sm text-muted-foreground">
							{hasCouponCode ? t("redeemingCoupon") : t("generatingQrCode")}
						</p>
					</div>
				)}

				{paymentStep === "qrcode" && pixQrCode && (
					<div className="space-y-4">
						<p className="text-sm text-muted-foreground">
							{t("scanQrCode")}
						</p>
						<div className="flex justify-center rounded-xl border border-border bg-muted/30 p-4">
							{/* biome-ignore lint/performance/noImgElement: base64 QR from Asaas API */}
							<img
								draggable={false}
								src={`data:image/png;base64,${pixQrCode.encodedImage}`}
								alt="QR Code PIX"
								className="h-48 w-48 object-contain select-none"
							/>
						</div>
						<p className="text-center text-xs text-muted-foreground">
							{t("waitingPayment")}
						</p>
						<div className="flex flex-col items-center gap-2">
							<Button
								type="button"
								variant="outline"
								size="sm"
								className="w-full"
								onClick={async () => {
									try {
										await navigator.clipboard.writeText(pixQrCode.payload);
										setPixCopied(true);
										window.setTimeout(() => setPixCopied(false), 2000);
									} catch {
										// fallback: não deu para copiar
									}
								}}
							>
								{pixCopied ? t("copied") : t("copyPixCode")}
							</Button>
							<span className="text-xs font-medium tabular-nums text-muted-foreground">
								{Math.floor(pixCountdownSeconds / 60)}:
								{(pixCountdownSeconds % 60).toString().padStart(2, "0")}
							</span>
						</div>
					</div>
				)}

				{paymentStep === "creating" && (
					<div className="flex flex-col items-center justify-center gap-4 py-8">
						{createdPageSlug ? (
							<>
								<CheckCircle2 className="h-12 w-12 text-primary" />
								<p className="text-center text-lg font-semibold text-primary">
									{t("pageCreated")}
								</p>
								<Link
									href={`/${createdPageSlug}`}
									className="cursor-pointer"
								>
									<p className="text-center text-sm text-muted-foreground hover:underline">
										{env.NEXT_PUBLIC_BASE_URL}/{createdPageSlug}
									</p>
								</Link>
								<Button asChild variant="default">
									<Link href={`/${createdPageSlug}`}>
										{t("goToPage")}
									</Link>
								</Button>
							</>
						) : (
							<>
								<p className="text-center text-lg font-semibold text-primary">
									{t("paymentConfirmed")}
								</p>
								<Loader2 className="h-10 w-10 animate-spin text-primary" />
								<p className="text-sm text-muted-foreground">
									{t("creatingPage")}
								</p>
								<div className="w-full max-w-xs space-y-2">
									<Progress value={uploadProgress} className="h-2" />
									{uploadError && (
										<p className="text-center text-sm text-destructive">
											{uploadError}
										</p>
									)}
								</div>
							</>
						)}
					</div>
				)}
			</div>
		</div>
	);
}
